import { motion } from 'framer-motion';

const Products = () => {
  const products = [
    {
      name: 'Kartu Pelajar',
      tagline: 'Smart Student Card',
      description:
        'Kartu pelajar digital dengan sistem scan barcode untuk akses informasi siswa secara instan.',
      features: [
        'Scan barcode untuk info siswa',
        'Presensi otomatis',
        'Nilai & akademik',
        'Info pembayaran SPP',
        'Notifikasi ke orang tua',
        'Free Aplikasi Selamanya',
      ],
      color: 'from-blue-500 to-blue-600',
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      ),
    },
    {
      name: 'Kartu Member',
      tagline: 'Loyalty Member Card',
      description:
        'Kartu member dengan aplikasi POS gratis untuk manajemen transaksi dan loyalitas customer.',
      features: [
        'Free Aplikasi POS',
        'Info member & preferensi',
        'Histori transaksi',
        'Sistem poin & reward',
        'Promo & diskon otomatis',
        'Laporan penjualan',
      ],
      color: 'from-purple-500 to-purple-600',
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      name: 'Kartu Nama',
      tagline: 'Digital Business Card',
      description:
        'Kartu nama digital dengan website profil profesional untuk networking yang lebih efektif.',
      features: [
        'Free Website Profile',
        'Detail kontak lengkap',
        'Katalog produk',
        'Link sosial media',
        'QR Code personal',
        'Analytics pengunjung',
      ],
      color: 'from-emerald-500 to-emerald-600',
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Pilih Solusi Untuk Anda</h2>
          <p className="section-subtitle">
            Tiga produk unggulan dengan fitur lengkap dan aplikasi gratis selamanya
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-3 gap-8"
        >
          {products.map((product, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="card relative overflow-hidden group hover:-translate-y-2 transition-transform duration-300"
            >
              {/* Gradient Header */}
              <div
                className={`absolute top-0 left-0 right-0 h-32 bg-gradient-to-br ${product.color} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}
              />

              {/* Icon */}
              <div
                className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${product.color} flex items-center justify-center text-white mb-6 shadow-lg`}
              >
                {product.icon}
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {product.name}
              </h3>
              <p className={`text-sm font-medium bg-gradient-to-r ${product.color} bg-clip-text text-transparent mb-4`}>
                {product.tagline}
              </p>
              <p className="text-gray-600 mb-6">{product.description}</p>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={`https://wa.me/6285235662062?text=Halo,%20saya%20tertarik%20dengan%20${encodeURIComponent(product.name)}`}
                className={`block w-full py-3 px-6 rounded-full font-semibold text-center transition-all duration-300 bg-gradient-to-r ${product.color} text-white hover:shadow-lg hover:scale-105`}
              >
                Pesan Sekarang
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
